/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Localized from '../../../shared/components/Localized';
import moment from 'moment';

import MaintenanceInfo from './MaintenanceInfo.js';
import MaintenanceHeader from './MaintenanceHeader.js';
import MaintenanceFeed from './MaintenanceFeed.js';
import { hyperforceDateWindow } from "../../../config";

const HYPERFORCE_WINDOW_RELEASE_DAYS = hyperforceDateWindow;

const SectionTitle = styled.div`
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    ta
    font-size: .75rem;
    line-height: 1.25;
    color: #54698d;
    text-transform: uppercase;
    letter-spacing: .0625rem;
    padding: .75rem 1rem;
    background: #f4f6f9;

    border-top: 1px solid #d8dde6;
    border-bottom: 1px solid #d8dde6;

    display: flex;
    align-items: baseline;
    justify-content: space-between;
`;

const SubText = styled.div`
  font-size: .6875rem;
  font-weight: 400;
  color: rgba(22, 50, 92, 0.5);
  text-transform: uppercase;
  letter-spacing: .0625rem;
`;

export default class Maintenance extends React.Component {
  static propTypes = {
    MaintenanceImpactTypes: PropTypes.array,
    Maintenance: PropTypes.object,
    submitModal: PropTypes.func,
    closeModal: PropTypes.func,
    instance: PropTypes.object,
    isOpen: PropTypes.bool,
    serviceKey: PropTypes.object,
    missingTranslations: PropTypes.func
  };

  render() {
    const { maintenance, missingTranslations } = this.props;
    const now = moment().utc();
    const oldestImpact = maintenance.MaintenanceImpacts[maintenance.MaintenanceImpacts.length - 1];
    const latestImpact = maintenance.MaintenanceImpacts[0];
    const startTime = oldestImpact ? oldestImpact.startTime : null;
    const endTime = latestImpact ? latestImpact.endTime : null;
    const resolved = endTime !== null;

    // Determine if maintenance is a release
    const isRelease = maintenance.message?.maintenanceType?.toLowerCase().includes('release');

    // If release update headers
    const modalHeader = isRelease ? 'releaseInformation' : 'maintenanceInformation';
    const historyHeader = isRelease ? 'releaseHistory' : 'maintenanceHistory';

    // Update labels if Hyperforce Core Release
    const isHyperforceCore = isRelease && maintenance.substrate?.toLowerCase().includes('hyperforce core');
    const daysUntilStart = maintenance.plannedStartTime.diff(now, 'days');
    const showAsStartDate = isHyperforceCore && daysUntilStart > HYPERFORCE_WINDOW_RELEASE_DAYS;

    // calculate before creating synthetic Resolve impact period
    const totalMilliseconds = maintenance.MaintenanceImpacts.reduce((duration, impact, index) => {
      const time = impact.endTime ? impact.endTime : now.clone();
      return duration + time.diff(impact.startTime);
    }, 0);

    return (
      <div>
        <MaintenanceHeader maintenance={maintenance} />
        <SectionTitle>
          <div>
            <Localized
              modelName={'maintenanceModal'}
              modelKey={modalHeader}
              modelAttribute={'header'}
              missingTranslations={missingTranslations}
            />
          </div>
          { maintenance.id &&
            <SubText>
              {`ID# ${maintenance.id}`}
            </SubText>
          }
        </SectionTitle>
        <MaintenanceInfo
          {...maintenance}
          startTime={startTime}
          endTime={endTime}
          resolved={resolved}
          status={maintenance.message.eventStatus}
          durationMS={totalMilliseconds}
          missingTranslations={missingTranslations}
          showPlannedDateOnly={showAsStartDate}
          showAsWindowTime={isHyperforceCore}
        />
        <SectionTitle>
          <Localized
            modelName={'maintenanceModal'}
            modelKey={historyHeader}
            modelAttribute={'header'}
            missingTranslations={missingTranslations}
          />
        </SectionTitle>
        <MaintenanceFeed
          maintenance={maintenance}
          missingTranslations={missingTranslations}
        />
      </div>
    );
  }
}
